import React, { useState, useCallback, useEffect } from 'react'
import { Formik, Form, Field } from 'formik'
import { useModal } from 'react-modal-hook'
import { useHistory } from 'react-router-dom'
import NumberFormat from 'react-number-format'
import { NeoOTP, NeoSuccess } from '../../../components/FormComponents'
import NeoButton from '../../../components/NeoButton/NeoButton'
import NeoInput from '../../../components/NeoInput/NeoInput'
import InputError from '../../../components/InputError/InputError'
import Loading from 'custom/assets/loaders/Loading'
import Modal from '../../../components/Modal/Modal'
import TermsAndCondition from '../../../components/TermsAndConditions/TermsAndCondition'
import LayoutWithAccountCard from '../../Layout/layout-account-card'
import { AddThirdPartyBeneficiarySchema } from '../../../schemas/transfers'
import BeneficiaryService from '../../../services/beneficiary.service'
import { useTranslation } from 'react-i18next'
import { ADD_BENEFICIARY_KEYS, BUTTON_KEYS } from '../../../utils/translation-constants'
import validationConfig from 'custom/config/validation.config.json'
const domesticBeneConfig = validationConfig['addBeneficiary']['domestic']
import config from 'custom/config/environment'

export default function AddDomesticBeneficiary() {
  const history = useHistory()
  const [stage, setStage] = useState(0)
  const [otpEnabled, setOtpEnabled] = useState(false)
  const [formValues, setFormValues] = useState({
    benName: '',
    nickName: '',
    accountNumber: '',
    confirmAccountNumber: '',
    bankName: '',
    // branchName: '',
    maxTransferAmount: '',
    transferAmount: undefined,
    isVisibleToGroup: false,
    beneEmail: '',
    beneMobileNo: '',
  })
  const [requestBanks, setRequestBanks] = useState([])
  const [message, setMessage] = useState('')
  const [loading, setLoading] = useState(false)
  const [otp, setOtp] = useState('')
  const [bankList, setBankList] = useState([])
  const [branchList, setBranchList] = useState([])
  const [filteredBankList, setFilteredBankList] = useState([])
  const [verifyDetails, setVerifyDetails] = useState({})
  const { t } = useTranslation()

  const [showModal, hideModal] = useModal(
    () => (
      <Modal>
        <div className='modal-error-body'>
          <h6 className='text-center'>{message}</h6>
          <div className='modal-btn-style'>
            <NeoButton
              tabIndex='1'
              mode='newDesign'
              type='button'
              text={t(BUTTON_KEYS.OK)}
              onClick={() => {
                hideModal()
              }}
            />
          </div>
        </div>
      </Modal>
    ),
    [message],
  )

  useEffect(() => {
    getAllBankDetails()
  }, [])

  const getAllBankDetails = useCallback(async () => {
    try {
      const response = await BeneficiaryService.getAllBankDetails({})
      if (response.status === 200) {
        const filteredData = response.data.reduce((items, obj) => {
          if (!items[obj['bankName']]) items[obj['bankName']] = []
          items[obj['bankName']].push(obj)
          return items
        }, {})
        const keys = Object.keys(filteredData)
        setBankList(keys)
        setFilteredBankList(filteredData)
      }
    } catch (err) {
      console.error(err)
    }
  }, [])

  const handleSelectBank = useCallback(
    async (selected) => {
      try {
        setBranchList(filteredBankList[selected])
      } catch (err) {
        console.error(err)
      }
    },
    [filteredBankList],
  )

  function renderSwitch() {
    switch (stage) {
      case 1:
        return renderForm()
      case 2:
        return renderConfirmation()
      case 3:
        return <NeoOTP otp={otp} setOTP={setOtp} onSubmit={onConfirm} loading={loading} />
      case 4:
        return <NeoSuccess message={message} linkPath='/transfers' />
      default:
        return renderForm()
    }
  }

  const onVerify = useCallback(
    async (values) => {
      try {
        if (values.transferAmount < config.MIN_TRANSFER_LIMIT) {
          return
        }
        const selectedBranch = branchList.filter((ele) => ele.bankName === values.bankName)[0]
        // let selectedBranch = {}
        // if (config.CLIENT_NAME !== 'NBS') {
        //   selectedBranch = branchList.filter((ele) => ele.branchDesc === values.branchName)[0]
        // } else {
        //   
        // }
        const reqParams = {
          ...values,
          address: selectedBranch?.address,
          bankCode: selectedBranch?.bankCode,
          bankName: selectedBranch?.bankName,
          bicCode: selectedBranch?.bicCode,
          branchDesc: selectedBranch?.branchDesc,
          countryCode: selectedBranch?.countryCode,
          sortCode: selectedBranch?.sortCode,
        }
        setFormValues(reqParams)
        setLoading(true)
        const response = await BeneficiaryService.verifyDomesticBeneficiary(reqParams)
        if (response.status === 200) {
          setOtpEnabled(response.data['otpEnable'])
          setVerifyDetails(response.data)
          setLoading(false)
          setStage(2)
        }
      } catch (err) {
        setLoading(false)
        setMessage(err?.data)
        //showModal()
      }
    },
    [showModal, branchList],
  )

  const onConfirm = useCallback(async () => {
    try {
      const reqParams = {
        ...verifyDetails,
        ...formValues,
        otp: otp,
      }
      setLoading(true)
      const response = await BeneficiaryService.confirmDomesticBeneficiary(reqParams)
      if (response.status === 200) {
        setLoading(false)
        setMessage(response.data.message)
        setStage(4)
      }
    } catch (err) {
      setLoading(false)
      setMessage(err?.data)
      setOtp('')
      //showModal()
    }
  }, [verifyDetails, formValues, otp])

  function renderForm() {
    return (
      <div>
        <Formik
          initialValues={formValues}
          enableReinitialize
          validationSchema={AddThirdPartyBeneficiarySchema}
          onSubmit={onVerify}>
          {({ isSubmitting, errors, touched, values, setFieldValue, handleChange }) => (
            <Form>
              <div className='row'>
                <div className='form-group col-12'>
                  <NeoInput
                    tabIndex='1'
                    value={values.benName}
                    showAsterisk={domesticBeneConfig['isBeneNameRequired']}
                    text={t(ADD_BENEFICIARY_KEYS.BENEFICIARY_NAME)}
                    name='benName'
                    onChange={handleChange}
                    minLength={2}
                    maxLength={32}
                    onKeyPress={(event) => {
                      if (
                        !(event.charCode >= 65 && event.charCode <= 90) &&
                        !(event.charCode >= 97 && event.charCode <= 122) &&
                        event.charCode !== 32
                      ) {
                        event.preventDefault()
                      }
                    }}
                  />
                  <InputError text={errors.benName} touched={touched.benName} />
                </div>
                <div className='form-group col-12'>
                  <NeoInput
                    tabIndex='2'
                    showAsterisk={domesticBeneConfig['isNickNameRequired']}
                    text={t(ADD_BENEFICIARY_KEYS.NICK_NAME)}
                    name='nickName'
                    value={values.nickName}
                    onChange={handleChange}
                    minLength={2}
                    maxLength={20}
                    onKeyPress={(event) => {
                      if (
                        !(event.charCode >= 65 && event.charCode <= 90) &&
                        !(event.charCode >= 97 && event.charCode <= 122) &&
                        event.charCode !== 32 &&
                        !(event.charCode >= 48 && event.charCode <= 57) &&
                        event.charCode !== 95
                      ) {
                        event.preventDefault()
                      }
                    }}
                  />
                  <InputError text={errors.nickName} touched={touched.nickName} />
                </div>
              </div>
              <div className='row'>
                <div className='form-group col-12'>
                  <NeoInput
                    tabIndex='3'
                    showAsterisk={domesticBeneConfig['isAccountNoRequired']}
                    text={t(ADD_BENEFICIARY_KEYS.ACCOUNT_NO)}
                    name='accountNumber'
                    // type='number'
                    minLength={5}
                    maxLength={23}
                    mode='password'
                    onChange={(value) => {
                      setFieldValue('accountNumber', value?.value)
                    }}
                  />
                  <InputError text={errors.accountNumber} touched={touched.accountNumber} />
                </div>
                <div className='form-group col-12'>
                  <NeoInput
                    tabIndex='4'
                    showAsterisk={domesticBeneConfig['isConfirmAccountNoRequired']}
                    text={t(ADD_BENEFICIARY_KEYS.CONFIRM_ACCOUNT_NO)}
                    //type='number'
                    mode='onlyNumber'
                    onChange={(value) => {
                      setFieldValue('confirmAccountNumber', value?.value)
                    }}
                    minLength={23}
                    maxLength={23}
                    name='confirmAccountNumber'
                  />
                  <InputError
                    text={errors.confirmAccountNumber}
                    touched={touched.confirmAccountNumber}
                  />
                </div>
              </div>
              <div className='row'>
                <div className='form-group col-12'>
                  <NeoInput
                    tabIndex='5'
                    showAsterisk={domesticBeneConfig['isBeneBankRequired']}
                    text={t(ADD_BENEFICIARY_KEYS.BENEFICIARY_BANK)}
                    name='bankName'
                    mode='select'
                    onChange={(e) => {
                      handleChange(e)
                      handleSelectBank(e.target.value)
                      setFieldValue('bankName', e.target.value)
                      setFieldValue('branchName', '')
                    }}
                    keyValue={null}
                    list={bankList}
                  />
                  <InputError text={errors.bankName} touched={touched.bankName} />
                </div>
                {/* <div className='form-group col-12'>
                  {config.CLIENT_NAME !== 'NBS' ? (
                    <NeoInput
                      tabIndex='6'
                      showAsterisk={domesticBeneConfig['isBranchRequired']}
                      text={t(ADD_BENEFICIARY_KEYS.BRANCH_NAME)}
                      name='branchName'
                      mode='select'
                      onChange={(e) => {
                        handleChange(e)
                        setFieldValue('branchName', e.target.value)
                      }}
                      list={branchList}
                      keyValue='branchDesc'
                    />
                  ) : (
                    <NeoInput
                      showAsterisk={domesticBeneConfig['isBranchRequired']}
                      text={t(ADD_BENEFICIARY_KEYS.BRANCH_NAME)}
                      name='branchName'
                      value={values.branchName}
                      onChange={handleChange}
                    />
                  )}
                  <InputError text={errors.branchName} touched={touched.branchName} />
                </div> */}
              </div>

              <div className='row'>
                <div className='form-group col-12'>
                  <NeoInput
                    tabIndex='7'
                    showAsterisk={domesticBeneConfig['isEmailRequired']}
                    text={t(ADD_BENEFICIARY_KEYS.BENE_EMAIL_ID)}
                    name='beneEmail'
                    value={values.beneEmail}
                    onChange={handleChange}
                    maxLength='30'
                  />
                  <InputError text={errors.beneEmail} touched={touched.beneEmail} />
                </div>
                <div className='form-group col-12'>
                  <NeoInput
                    tabIndex='8'
                    showAsterisk={domesticBeneConfig['isMobileNoRequired']}
                    text={t(ADD_BENEFICIARY_KEYS.BENE_MOBILE_NO)}
                    onChange={(value) => {
                      setFieldValue('beneMobileNo', value?.value)
                    }}
                    mode='onlyNumber'
                    name='beneMobileNo'
                    minLength={8}
                    maxLength={15}
                  />
                  <InputError text={errors.beneMobileNo} touched={touched.beneMobileNo} />
                </div>
              </div>
              <div className='row'>
                <div className='form-group col-12'>
                  <NeoInput
                    tabIndex='9'
                    showAsterisk={domesticBeneConfig['isTransferLimitRequired']}
                    text={t(ADD_BENEFICIARY_KEYS.TRANSFER_LIMIT)}
                    type='number'
                    mode='amount'
                    value={values.maxTransferAmount}
                    onChange={(value) => {
                      setFieldValue('transferAmount', value?.floatValue)
                      setFieldValue('maxTransferAmount', value?.value)
                    }}
                    name='maxTransferAmount'
                  />
                  <InputError text={errors.maxTransferAmount} touched={touched.maxTransferAmount} />
                  <InputError
                    text={`${t(ADD_BENEFICIARY_KEYS.TRANSFER_LIMIT_TXT)} ${
                      config.MIN_TRANSFER_LIMIT
                    }.00`}
                    touched={values.transferAmount < config.MIN_TRANSFER_LIMIT}
                  />
                </div>
              </div>

              <div class='form-check'>
                <Field
                  tabIndex={'10'}
                  as='input'
                  id='visibleGroup'
                  type='checkbox'
                  className='form-check-input'
                  name='isVisibleToGroup'
                />
                <label class='form-check-label' for='visibleGroup'>
                  {t(ADD_BENEFICIARY_KEYS.VISIBLE_GROUP)}
                </label>
              </div>

              <div className='mt-3'>
                {loading ? (
                  renderLoading()
                ) : (
                  <NeoButton
                    tabIndex='11'
                    type='submit'
                    text={t(BUTTON_KEYS.SUBMIT)}
                    disabled={isSubmitting}
                    mode='newDesign'
                  />
                )}
              </div>
              <div className='back-cancel-btn-container'>
                <button
                  tabIndex={'12'}
                  className='back-cancel-btn btn btn-sm new-design-warning-text-color'
                  onClick={() => history.goBack()}>
                  {t(BUTTON_KEYS.CANCEL)}
                </button>
              </div>
            </Form>
          )}
        </Formik>
      </div>
    )
  }

  function renderConfirmation() {
    return (
      <div className='d-flex flex-column confirmation'>
        <div>
          <h6 className='label'>{t(ADD_BENEFICIARY_KEYS.BENEFICIARY_NAME)}</h6>
          <h6 className='value'>{formValues.benName}</h6>
        </div>
        <div>
          <h6 className='label'>{t(ADD_BENEFICIARY_KEYS.NICK_NAME)}</h6>
          <h6 className='value'>{formValues.nickName}</h6>
        </div>

        <div>
          <h6 className='label'>{t(ADD_BENEFICIARY_KEYS.ACCOUNT_NO)}</h6>
          <h6 className='value'>{formValues.accountNumber}</h6>
        </div>

        <div>
          <h6 className='label'>{t(ADD_BENEFICIARY_KEYS.BENEFICIARY_BANK)}</h6>
          <h6 className='value'>{formValues.bankName}</h6>
        </div>

        {/* <div>
          <h6 className='label'>{t(ADD_BENEFICIARY_KEYS.BRANCH_NAME)}</h6>
          <h6 className='value'>{formValues.branchName}</h6>
        </div> */}

        <div>
          <h6 className='label'>{t(ADD_BENEFICIARY_KEYS.BENE_EMAIL_ID)}</h6>
          <h6 className='value'>{formValues.beneEmail}</h6>
        </div>

        <div>
          <h6 className='label'>{t(ADD_BENEFICIARY_KEYS.BENE_MOBILE_NO)}</h6>
          <h6 className='value'>{formValues.beneMobileNo}</h6>
        </div>

        <div>
          <h6 className='label'>{t(ADD_BENEFICIARY_KEYS.TRANSFER_LIMIT)}</h6>
          <h6 className='value'>
            <NumberFormat
              value={formValues.maxTransferAmount}
              displayType={'text'}
              thousandSeparator={true}
              prefix={''}
              fixedDecimalScale={true}
              decimalScale={2}
              allowEmptyFormatting
            />
          </h6>
        </div>

        <div>
          <h6 className='label'>{t(ADD_BENEFICIARY_KEYS.VISIBLE_GROUP)}</h6>
          <h6 className='value'>{formValues.isVisibleToGroup ? 'Yes' : 'No'}</h6>
        </div>

        <div>
          <TermsAndCondition module='BENEFICIARY' />
        </div>

        <div className='mt-3'>
          {loading ? (
            renderLoading()
          ) : (
            <NeoButton
              type='button'
              mode='newDesign'
              text={t(BUTTON_KEYS.CONFIRM)}
              onClick={() => (otpEnabled ? setStage(3) : onConfirm())}
            />
          )}
        </div>
        <div className='back-cancel-style' onClick={() => setStage(1)}>
          {' '}
          {t(BUTTON_KEYS.BACK)}{' '}
        </div>
      </div>
    )
  }

  function renderLoading() {
    return (
      <div className='mx-4 rounded d-flex justify-content-center align-items-center'>
        <Loading width='50px' height='50px' />
      </div>
    )
  }

  return (
    <LayoutWithAccountCard breadCumKey='BENE_DOMESTIC'>
      <div className='new-design-form-style'>
        <div className='heading-style'>{t(ADD_BENEFICIARY_KEYS.DOMESTIC_BENE_HEADER)}</div>
        {renderSwitch()}
      </div>
    </LayoutWithAccountCard>
  )
}
